import * as Evaluation from '@cck/common/dist/data/Evaluation'
import { getKoName } from '@cck/common/dist/data/Translation'
import _ from 'lodash'

import { writeExcel } from '../../base/utils/ExcelUtils'
import { getDefectSummary } from '../evaluation/common/DesignDefectUtils'
import { getResultStatus, getResultStatusColumns } from './ResultStatusUtils'

function convertTableContent(content: Evaluation.TableContent, split?: boolean): string[][] {
  const data: string[][] = []
  if (!split) {
    data.push(content.headerRow)
  } else {
    const firstHeaders: string[] = []
    const secondHeaders: string[] = []
    _.forEach(content.headerRow, (header) => {
      const splitHeader = _.split(header, '/')
      if (splitHeader.length > 1) {
        firstHeaders.push(splitHeader[0])
        secondHeaders.push(splitHeader[1])
      } else {
        firstHeaders.push(' ')
        secondHeaders.push(splitHeader[0])
      }
    })
    data.push(firstHeaders)
    data.push(secondHeaders)
  }

  _.forEach(content.rowObjectList, (row) => {
    data.push(_.map(content.headerRow, (header) => _.get(row, header, '')))
  })
  return data
}

function downloadOperationSummary(
  evaluations: Evaluation.EvaluationItem[],
  data: (string | number)[][]
): void {
  const defectEvaluations: Evaluation.EvaluationItem[] = _.filter(evaluations, (evaluation) => {
    // 미비점이 존재할 경우
    return (
      evaluation.state.weakState === '3' ||
      evaluation.state.weakState === '4' ||
      evaluation.state.weakState === '5'
    )
  })

  _.forEach(defectEvaluations, (evaluation) => {
    if (!Evaluation.isOperationEvaluation(evaluation.content.data)) {
      return
    }

    const weakStateTable = _(evaluation.state.weakStateData as Evaluation.OperationDefect)
      .filter((weakState) => !_.isEmpty(_.get(weakState, 'answer')))
      .map((weakState, key) => {
        if (Evaluation.isOperationWeakStateItem(weakState)) {
          return [key, weakState.question, weakState.answer, weakState.reason]
        }
        return [key]
      })
      .value()

    const evalData: Evaluation.OperationEvaluationData = evaluation.content.data
    data.push(
      ...[
        [],
        [],
        [getKoName('control', 'id'), evaluation.controlId],
        [getKoName('control', 'narrative'), evaluation.content.control.narrative],
        [getKoName('control', 'populationDescription'), evalData.populationDescription],
        [getKoName('control', 'populationCompleteness'), evalData.populationDescription],
        [getKoName('control', 'samplingMethod'), evalData.samplingMethod],
        [getKoName('control', 'samplingCount'), evalData.samplingCount],
        [getKoName('control', 'samplingAttributeCount'), evalData.samplingAttributeCount],
        [],
        ['테스트 수행 기준'],
        ...convertTableContent(evalData.attributeContents),
        [],
        ['테스트 수행 내역'],
        ...convertTableContent(evalData.testContents, true),
        [],
        [getKoName('control', 'populationCount'), evalData.populationCount],
        [getKoName('control', 'samplingCount'), evalData.samplingCount],
        [getKoName('control', 'validCount'), evalData.validCount],
        [getKoName('control', 'invalidCount'), evalData.invalidCount],
        [getKoName('control', 'testResult'), evalData.testResult],
        [],
        ['미비점평가', '질의', '답변', '근거'],
        ...weakStateTable
      ]
    )
  })

  writeExcel('결과 요약', { 운영평가: data })
}

function downloadDesignSummary(
  evaluations: Evaluation.EvaluationItem[],
  data: (string | number)[][]
) {
  const defectEvaluations: Evaluation.EvaluationItem[] = _.filter(evaluations, (evaluation) => {
    // 수정사항이 있는 사항
    return evaluation.state.weakState === Evaluation.DesignWeakState.Modification
  })

  _.forEach(defectEvaluations, (evaluation) => {
    const defectSummary = getDefectSummary(evaluation)

    data.push(
      ...[
        [],
        [],
        [getKoName('control', 'id'), evaluation.controlId],
        [getKoName('control', 'narrative'), evaluation.content.control.narrative],
        ['구분', '기존 통제 정보', '수정대상', '수정근거'],
        ..._.map(defectSummary, (row) => [row.category, row.key, row.content, row.evidence])
      ]
    )
  })
  writeExcel('결과 요약', { 설계평가: data })
}

export function downloadSummary(
  type: Evaluation.Type,
  summary: Evaluation.Base,
  evaluations: Evaluation.EvaluationItem[]
): void {
  const resultStatusColumns = getResultStatusColumns(type)
  const data: (string | number)[][] = []
  data.push(
    ...[
      ['평가명', summary.name],
      ['평가대상기간', summary.period],
      [],
      _.map(resultStatusColumns, (column) => column.headerName || '')
    ]
  )

  const resultStatusList = getResultStatus(type, evaluations)
  _.forEach(resultStatusList, (status) => {
    data.push(_.map(resultStatusColumns, (column) => _(status).get(column.field || '', '')))
  })

  if (type === Evaluation.Type.Operation) {
    downloadOperationSummary(evaluations, data)
  } else {
    downloadDesignSummary(evaluations, data)
  }
}
